import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { IconButton } from '@startuptools/ui/IconButton';
import { Stack } from '@startuptools/ui/Stack';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import {
  GqlCompany,
  GqlCreateIdentityInput,
  GqlCurrency,
  GqlOptionsRegistration,
  GqlWarrantProgram,
} from '../../../../../graphql/base-types.graphql';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMemo, useState } from 'react';
import { EditWarrantParticipant } from './or-subscribers/Edit/types';
import { orderBy } from 'lodash-es';
import { BigNumber } from 'bignumber.js';
import { PaymentKind } from '@startuptools/common/participants';
import { Table } from '@startuptools/ui/Table';
import { useWppDeleteMutation } from '../../../../../graphql/react-operations';
import { OptionsRegistrationsDocument } from '../../../OptionsRegistration.graphql';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { DetailState, InputState, JuridicalKind } from '@startuptools/business-ui/identity/types';
import { EditParticipantDialog } from './or-subscribers/Edit';
import { AlertConfirmDialog } from '../../../../../components/simple-dialogs/AlertConfirmDialog';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { StepOrSubscribersDocument } from './or-subscribers/or-subscribers.graphql';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';

export const WppTable = ({
  warrantProgram,
  optionsRegistrations,
  paymentKind,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    'id' | 'companyId' | 'shareClasses' | 'usesStepper' | 'warrantStartDate' | 'warrantKind' | 'discountPricePerWarrant'
  > & {
    company: Pick<GqlCompany, 'id' | 'allowShortQeso' | 'organizationNumber'>;
  };
  optionsRegistrations: (Pick<GqlOptionsRegistration, 'id' | 'createdAt'> & {
    participants: EditWarrantParticipant[];
  })[];
  paymentKind: PaymentKind.Offset | PaymentKind.NonCash;
}) => {
  const { i18n } = useLingui();
  const [identity, setIdentity] = useState<GqlCreateIdentityInput | null>(null);
  const [participant, setParticipant] = useState<EditWarrantParticipant | null>(null);
  const [deleteWpp, setDeleteWpp] = useState<EditWarrantParticipant | null>(null);
  const [showIdentityDialog, setShowIdentityDialog] = useState(false);

  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteWpp] = useWppDeleteMutation({ refetchQueries: [OptionsRegistrationsDocument] });

  const { participants, totalOptions, totalClaim } = useMemo(() => {
    const wpps = optionsRegistrations.reduce(
      (prev, or) => [...prev, ...or.participants.filter(wpp => wpp.paymentKind === paymentKind)],
      [] as EditWarrantParticipant[],
    );
    const participants = orderBy(wpps, wpp => wpp.createdAt, 'desc');
    const totalClaim = participants
      .filter(wpp => Boolean(wpp.claim))
      // We want to sum the rounded number in order to make the total display as expected
      .reduce((sum, wpp) => sum.plus(Number(wpp.claim!).toFixed(2)), new BigNumber(0))
      .toNumber();
    const totalOptions = participants
      .filter(wpp => Boolean(wpp.quantity))
      .reduce((sum, wpp) => sum.plus(wpp.quantity!), new BigNumber(0))
      .toNumber();

    return { participants, totalOptions, totalClaim };
  }, [optionsRegistrations, paymentKind]);

  const optionsRegistration = optionsRegistrations.length
    ? orderBy(optionsRegistrations, or => or.createdAt, 'asc').at(0)
    : null;

  const pricePerOption = new BigNumber(warrantProgram.discountPricePerWarrant ?? '0');

  return (
    <>
      <Box>
        <Button variant="outlined" onClick={() => setShowIdentityDialog(true)}>
          <Trans id="Add Subscriber" />
        </Button>
      </Box>
      <Table>
        <thead>
          <tr>
            <th>
              <Trans id="Creditor" />
            </th>
            <th align="right">
              <Trans id="Options" />
            </th>
            <th align="right">
              {paymentKind === PaymentKind.Offset ? <Trans id="Claim" /> : <Trans id="Total Value" />}
            </th>
            <th align="right" />
          </tr>
        </thead>
        <tbody>
          {participants.map(wpp => (
            <tr key={wpp.id}>
              <td>
                <Identity identity={wpp.identity} />
              </td>
              <td align="right">
                <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                  {wpp.quantity}
                </NumberFormat>
              </td>
              <td align="right">
                <NumberFormat locale={i18n.locale} variant="currency" currency={GqlCurrency.Sek}>
                  {paymentKind === PaymentKind.Offset ? wpp.claim : pricePerOption.times(wpp.quantity || '0')}
                </NumberFormat>
              </td>

              <td align="right">
                <Stack direction="row" justifyContent="end">
                  <ButtonGroup>
                    <IconButton color="primary" onClick={() => setParticipant(wpp)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="danger" onClick={() => setDeleteWpp(wpp)}>
                      <DeleteIcon />
                    </IconButton>
                  </ButtonGroup>
                </Stack>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th />
            <th align="right">
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {totalOptions}
              </NumberFormat>
            </th>
            <th align="right">
              <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                {paymentKind === PaymentKind.Offset ? totalClaim : pricePerOption.times(totalOptions).toString()}
              </NumberFormat>
            </th>
            <th />
          </tr>
        </tfoot>
      </Table>
      {showIdentityDialog && (
        <AddIdentity
          headline={<Trans id="Add subscriber" />}
          juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          detailFieldsSettings={{ email: DetailState.Optional }}
          handleCreate={identity => {
            setIdentity(identity);
            setShowIdentityDialog(false);
          }}
          onClose={() => {
            setShowIdentityDialog(false);
          }}
        />
      )}
      {(participant || identity) && (
        <ErrorBoundary showModal>
          <EditParticipantDialog
            optionRegistrationId={optionsRegistration!.id}
            paymentKind={paymentKind}
            closeDialog={() => {
              setIdentity(null);
              setParticipant(null);
            }}
            identity={participant?.identity ?? identity!}
            warrantProgram={warrantProgram}
            participant={participant ?? undefined}
            company={warrantProgram.company}
            refetchQueries={[StepOrSubscribersDocument, OptionsRegistrationsDocument, WarrantProgramDocument]}
          />
        </ErrorBoundary>
      )}
      {deleteWpp && (
        <AlertConfirmDialog
          onClose={() => setDeleteWpp(null)}
          title={<Trans id="Are you sure?" />}
          content={<Trans id="Are you sure you want to delete the participant?" />}
          onConfirm={() => doDeleteWpp({ variables: { companyId: warrantProgram.companyId, id: deleteWpp.id } })}
        />
      )}
    </>
  );
};
